import { apiRequestQueue } from '../utils/requestQueue';
import AnalyticsApiService from './analyticsApi';
import { processResponseData, convertLegacyGcsUrlToProxy } from '../utils/profileImage';

interface ProfileImageCacheEntry {
  imageUrl: string | null;
  timestamp: number;
  loading: boolean;
  callbacks: Array<(url: string | null) => void>;
}

export class ProfileImageCache {
  private static cache: Map<string, ProfileImageCacheEntry> = new Map();
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  /**
   * Get profile image URL for a participant in a conversation
   * This method batches requests to prevent overwhelming the backend
   */
  static async getProfileImage(
    participantId: string,
    conversationId: number
  ): Promise<string | null> {
    const cacheKey = `${conversationId}-${participantId}`;
    const cached = this.cache.get(cacheKey);
    
    // Check if we have a valid cached entry
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      if (cached.loading) {
        // If still loading, wait for it
        return new Promise((resolve) => {
          cached.callbacks.push(resolve);
        });
      }
      return cached.imageUrl;
    }
    
    // Create new cache entry
    const entry: ProfileImageCacheEntry = {
      imageUrl: null,
      timestamp: Date.now(),
      loading: true,
      callbacks: []
    };
    
    this.cache.set(cacheKey, entry);
    
    try {
      // Fetch profile images for the conversation using the existing API method
      const pfps = await AnalyticsApiService.getConversationPFPs(conversationId);
      
      // Process the response to ensure URLs are converted
      const processedPfps = processResponseData(pfps);
      
      // Find the specific participant's image
      const participant = processedPfps.find((p: any) => p.id === participantId);
      const imageUrl = participant?.pfp_url || null;
      
      // Additional conversion as safety net
      const finalImageUrl = imageUrl ? convertLegacyGcsUrlToProxy(imageUrl) : null;
      
      // Update cache with converted URL
      entry.imageUrl = finalImageUrl;
      entry.loading = false;
      entry.timestamp = Date.now();
      
      // Notify all waiting callbacks with converted URL
      entry.callbacks.forEach(cb => cb(finalImageUrl));
      entry.callbacks = [];
      
      return finalImageUrl;
    } catch (error: any) {
      // Only log non-404 errors to reduce console spam
      if (error.status !== 404) {
        console.error('Failed to fetch profile image:', error);
      }
      
      // Update cache with failure
      entry.loading = false;
      entry.callbacks.forEach(cb => cb(null));
      entry.callbacks = [];
      
      return null;
    }
  }
  
  /**
   * Clear the cache
   */
  static clearCache() {
    this.cache.clear();
  }
  
  /**
   * Preload profile images for multiple conversations
   * This helps reduce the number of requests when displaying conversation lists
   */
  static async preloadConversationImages(conversationIds: number[]) {
    // Limit to first 5 conversations to prevent overwhelming the backend
    const limitedIds = conversationIds.slice(0, 5);
    
    // Preload in sequence with small delay
    for (const conversationId of limitedIds) {
      try {
        const pfps = await AnalyticsApiService.getConversationPFPs(conversationId);
        
        // Process the response to ensure URLs are converted
        const processedPfps = processResponseData(pfps);
        
        // Cache all participant images for this conversation
        processedPfps.forEach((p: any) => {
          const cacheKey = `${conversationId}-${p.id}`;
          // Additional conversion as safety net
          const finalImageUrl = p.pfp_url ? convertLegacyGcsUrlToProxy(p.pfp_url) : null;
          
          this.cache.set(cacheKey, {
            imageUrl: finalImageUrl,
            timestamp: Date.now(),
            loading: false,
            callbacks: []
          });
        });
        // Small delay between requests
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error: any) {
        // Only log non-404 errors to reduce console spam (endpoint may not exist)
        if (error.status !== 404) {
          console.warn(`Failed to preload images for conversation ${conversationId}:`, error);
        }
      }
    }
  }
}
